import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Users, MousePointer, FileText, Bell, TrendingUp, 
  Calendar, Building2, Activity, Eye, BarChart3, ArrowLeft
} from 'lucide-react';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function StatCard({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="mt-3 flex items-center text-sm">
            <TrendingUp className={`w-4 h-4 mr-1 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={trend >= 0 ? 'text-green-500' : 'text-red-500'}>
              {trend >= 0 ? '+' : ''}{trend}%
            </span>
            <span className="text-muted-foreground ml-1">vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [useCustomDates, setUseCustomDates] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [period, useCustomDates, customDateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      let params = {};
      
      if (useCustomDates && customDateRange.start && customDateRange.end) {
        params = {
          start_date: new Date(customDateRange.start).toISOString(),
          end_date: new Date(customDateRange.end).toISOString()
        };
      } else {
        params = { days: parseInt(period) };
      }
      
      const data = await api.getAnalyticsDashboard(params);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No analytics data available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Date Range Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Time Period</Label>
              <Select value={period} onValueChange={(v) => { setPeriod(v); setUseCustomDates(false); }}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">or</div>
            <div className="space-y-2">
              <Label>Custom Range</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  className="w-40"
                />
                <Input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  className="w-40"
                />
                <Button 
                  variant="outline" 
                  onClick={() => setUseCustomDates(true)}
                  disabled={!customDateRange.start || !customDateRange.end}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={analytics.user_engagement.total_users}
          subtitle={`${analytics.user_engagement.active_users} active`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Tile Clicks"
          value={analytics.tile_analytics.total_clicks}
          subtitle="Total interactions"
          icon={MousePointer}
          color="green"
        />
        <StatCard
          title="Article Views"
          value={analytics.content_performance.article_views}
          subtitle="Content engagement"
          icon={FileText}
          color="yellow"
        />
        <StatCard
          title="Notification Open Rate"
          value={`${analytics.notification_analytics.open_rate}%`}
          subtitle={`${analytics.notification_analytics.total_opens} of ${analytics.notification_analytics.total_sent}`}
          icon={Bell}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Activity Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Daily Activity Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_id" tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="logins" stroke="#2563EB" name="Logins" />
                <Line type="monotone" dataKey="tile_clicks" stroke="#10B981" name="Tile Clicks" />
                <Line type="monotone" dataKey="article_views" stroke="#F59E0B" name="Article Views" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Tiles */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Top Tiles by Clicks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.tile_analytics.top_tiles.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.tile_analytics.top_tiles} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="title" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="clicks" fill="#2563EB" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No tile click data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Engagement & Articles Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Engagement */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-accent rounded-lg">
                <span className="text-sm font-medium">Engagement Rate</span>
                <span className="text-2xl font-bold text-primary">
                  {analytics.user_engagement.engagement_rate}%
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg text-center">
                  <p className="text-2xl font-bold">{analytics.user_engagement.total_users}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <p className="text-2xl font-bold">{analytics.user_engagement.active_users}</p>
                  <p className="text-xs text-muted-foreground">Active Users</p>
                </div>
                <div className="p-3 border rounded-lg text-center col-span-2">
                  <p className="text-2xl font-bold">{analytics.user_engagement.login_count}</p>
                  <p className="text-xs text-muted-foreground">Total Logins</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Articles */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Top Articles by Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.content_performance.top_articles.length > 0 ? (
              <div className="space-y-3">
                {analytics.content_performance.top_articles.map((article, index) => (
                  <div key={article.article_id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{article.title}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{article.views}</p>
                      <p className="text-xs text-muted-foreground">views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                No article view data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SuperAdminAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [useCustomDates, setUseCustomDates] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [period, useCustomDates, customDateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      let params = {};
      
      if (useCustomDates && customDateRange.start && customDateRange.end) {
        params = {
          start_date: new Date(customDateRange.start).toISOString(),
          end_date: new Date(customDateRange.end).toISOString()
        };
      } else {
        params = { days: parseInt(period) };
      }
      
      const data = await api.getSuperAdminAnalytics(params);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No analytics data available
      </div>
    );
  }

  // Prepare pie chart data for plan distribution
  const planData = Object.entries(analytics.plan_distribution).map(([plan, count]) => ({
    name: plan.charAt(0).toUpperCase() + plan.slice(1),
    value: count
  }));

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Date Range Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Time Period</Label>
              <Select value={period} onValueChange={(v) => { setPeriod(v); setUseCustomDates(false); }}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">or</div>
            <div className="space-y-2">
              <Label>Custom Range</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  className="w-40"
                />
                <Input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  className="w-40"
                />
                <Button 
                  variant="outline" 
                  onClick={() => setUseCustomDates(true)}
                  disabled={!customDateRange.start || !customDateRange.end}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Customers"
          value={analytics.platform_stats.total_customers}
          subtitle={`${analytics.platform_stats.active_customers} active`}
          icon={Building2}
          color="blue"
        />
        <StatCard
          title="New Customers"
          value={analytics.platform_stats.new_customers}
          subtitle="This period"
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Total Users"
          value={analytics.platform_stats.total_users}
          subtitle="Across all customers"
          icon={Users}
          color="yellow"
        />
        <StatCard
          title="Total Events"
          value={analytics.platform_stats.total_events}
          subtitle="This period"
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Platform Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Platform Activity Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="events" stroke="#2563EB" name="Total Events" />
                <Line type="monotone" dataKey="unique_users" stroke="#10B981" name="Unique Users" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Customers by Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={planData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {planData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Customers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Top Customers by Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.top_customers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {analytics.top_customers.map((customer, index) => (
                <div key={customer.customer_id} className="flex items-center gap-3 p-4 border rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{customer.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{customer.plan} plan</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{customer.events}</p>
                    <p className="text-xs text-muted-foreground">events</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No customer activity data yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Platform Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Platform Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-accent rounded-lg text-center">
              <p className="text-3xl font-bold">{analytics.platform_stats.total_tiles}</p>
              <p className="text-sm text-muted-foreground">Total Tiles</p>
            </div>
            <div className="p-4 bg-accent rounded-lg text-center">
              <p className="text-3xl font-bold">{analytics.platform_stats.total_articles}</p>
              <p className="text-sm text-muted-foreground">Total Articles</p>
            </div>
            <div className="p-4 bg-accent rounded-lg text-center">
              <p className="text-3xl font-bold">
                {analytics.platform_stats.total_customers > 0 
                  ? Math.round(analytics.platform_stats.total_users / analytics.platform_stats.total_customers)
                  : 0}
              </p>
              <p className="text-sm text-muted-foreground">Avg Users/Customer</p>
            </div>
            <div className="p-4 bg-accent rounded-lg text-center">
              <p className="text-3xl font-bold">
                {analytics.platform_stats.total_customers > 0
                  ? Math.round(analytics.platform_stats.active_customers / analytics.platform_stats.total_customers * 100)
                  : 0}%
              </p>
              <p className="text-sm text-muted-foreground">Customer Activity Rate</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
        <p className="text-muted-foreground">
          {user?.role === 'super_admin' 
            ? 'Platform-wide analytics and insights'
            : 'Your organisation\'s analytics and insights'}
        </p>
      </div>
      
      {user?.role === 'super_admin' ? <SuperAdminAnalytics /> : <AdminAnalytics />}
    </div>
  );
}
